import { makeStyles } from '@material-ui/core/styles'
import { DateCell, FixedPointCell, IntegerCell, NullableTextCell } from 'core/table/cells'
import { DYNAMIC_TEXT, DYNAMIC_NUMBER, getMultifieldDynamicTextFilter } from 'core/table/filters'
import { filter, get } from 'lodash'
import { Organization } from 'organization/types'
import React, { useMemo } from 'react'
import { OrganizationContactCell } from '../components/OrganizationContactCell'
import { OrganizationLinkCell } from '../components/OrganizationLinkCell'
import { CellProps } from 'react-table'

export const useStyles = makeStyles(theme => ({
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    cursor: 'pointer',
  },
  selectColumn: {
    '&>div': {
      width: '46px',
    },
  },
  nameColumn: {
    '&>div': {
      width: '160px',
    },
  },
  createdAtColumn: {
    '&>div': {
      width: '80px',
    },
  },
  countryColumn: {
    '&>div': {
      width: '110px',
    },
  },
  totalMembersColumn: {
    '&>div': {
      width: '80px',
    },
  },
  totalVolunteersColumn: {
    '&>div': {
      width: '80px',
    },
  },
  totalActivitiesColumn: {
    '&>div': {
      width: '80px',
    },
  },
  totalHoursVolunteeredColumn: {
    '&>div': {
      width: '80px',
    },
  },
  websiteColumn: {
    '&>div': {
      width: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  contactColumn: {
    '&>div': {
      width: '110px',
    },
  },
  smsCreditsColumn: {
    '&>div': {
      width: '110px',
    },
  },
  emailCreditsColumn: {
    '&>div': {
      width: '110px',
    },
  },
}))

const organizationNameFilter = getMultifieldDynamicTextFilter(['name.orgName'])

//Find column interface
// interface Column {
//   id: string
//   Header: any
//   accessor: string
//   filter: string
// }

export default function useOrganizationColumns() {
  const classes = useStyles()

  return useMemo(
    () =>
      filter<any>(
        [
          {
            id: 'name',
            Header: 'Name',
            accessor: (organization: Organization) => ({
              orgName: organization.name,
              id: organization.id,
            }),
            filter: organizationNameFilter,
            className: classes.addressColumn,
            Cell: OrganizationLinkCell,
          },
          {
            id: 'createdAt',
            Header: 'Created',
            accessor: 'createdAt' as keyof Organization,
            Cell: DateCell,
            className: classes.createdAtColumn,
          },
          {
            id: 'country',
            Header: 'Country',
            filter: DYNAMIC_TEXT,
            accessor: 'address.country' as keyof Organization,
            className: classes.countryColumn,
          },
          {
            id: 'totalMembers',
            Header: 'Members',
            filter: DYNAMIC_NUMBER,
            accessor: 'totalMembers' as keyof Organization,
            Cell: IntegerCell,
            className: classes.totalMembersColumn,
          },
          {
            id: 'totalVolunteers',
            Header: 'Volunteers',
            filter: DYNAMIC_NUMBER,
            accessor: 'totalVolunteers' as keyof Organization,
            Cell: IntegerCell,
            className: classes.totalVolunteersColumn,
          },
          {
            id: 'totalActivities',
            Header: 'Activities',
            filter: DYNAMIC_NUMBER,
            accessor: 'totalActivities' as keyof Organization,
            Cell: IntegerCell,
            className: classes.totalActivitiesColumn,
          },
          {
            id: 'totalHoursVolunteered',
            Header: 'Hours',
            filter: DYNAMIC_NUMBER,
            accessor: 'totalHoursVolunteered' as keyof Organization,
            Cell: FixedPointCell,
            className: classes.totalHoursVolunteeredColumn,
          },
          {
            id: 'website',
            Header: 'Website',
            filter: DYNAMIC_TEXT,
            accessor: 'website' as keyof Organization,
            className: classes.websiteColumn,
            Cell: (props: CellProps<Organization>) => {
              const website = get(props.row.original, 'website')
              if (!!website) {
                return (
                  <a className={classes.link} href={website} target="_blank" rel="noopener noreferrer">
                    {website}
                  </a>
                )
              }
              return NullableTextCell(props)
            },
          },
          {
            id: 'organizationContact',
            Header: 'Contact',
            accessor: (organization: Organization) => ({
              website: organization.website,
              address: organization.address,
              email: organization.replyToEmail,
            }),
            Cell: OrganizationContactCell,
            disableSortBy: true,
            disableFilters: true,
            className: classes.contactColumn,
          },
          {
            id: 'smsCredits',
            Header: 'SMS Credits',
            filter: DYNAMIC_NUMBER,
            accessor: 'smsCredits' as keyof Organization,
            Cell: IntegerCell,
            className: classes.smsCreditsColumn,
          },
          {
            id: 'emailCredits',
            Header: 'Email Credits',
            filter: DYNAMIC_NUMBER,
            accessor: 'emailCredits' as keyof Organization,
            Cell: IntegerCell,
            className: classes.emailCreditsColumn,
          },
        ],
        column => !column.hideColumn,
      ),
    [
      classes.addressColumn,
      classes.contactColumn,
      classes.countryColumn,
      classes.createdAtColumn,
      classes.emailCreditsColumn,
      classes.link,
      classes.smsCreditsColumn,
      classes.totalActivitiesColumn,
      classes.totalHoursVolunteeredColumn,
      classes.totalMembersColumn,
      classes.totalVolunteersColumn,
      classes.websiteColumn,
    ],
  )
}
